import { goBack } from "assets/icons";
import styled from "@emotion/styled";
import { Card, Col, Form, Row, Tabs, Typography, message } from "antd";
import { useNavigate } from "react-router-dom";
import { useGetMasterProvince } from "hooks/useMasterData";
import { FC, useEffect, useState } from "react";
import { AddressData } from "../CreateAgent/AddressComponents/interface/address.interface";
import FooterCreateAgent from "../CreateAgent/FooterCreateAgent";
import MainCreateAgent from "../CreateAgent/AgentComponents/MainCreateAgent";
import MainCreateAgentAddress from "../CreateAgent/AddressComponents/MainCreateAgentAddress";
import MainCreateAgentOrder from "../CreateAgent/OrderComponents/MainCreateAgentOrder";
import { useGetAgentById } from "hooks/useAgents";
import { useUpdateAgent } from "hooks/useFactory";
import AgentParcelTab from "../ManageAgentById/AgentParcelTab";
import MainCreateBankingAgent from "../CreateAgent/BankingComponents/MainCreateBankingAgent";
import MainCreateOrderTax from "components/OrderManage/CreateOrders/TaxSection/MainCreateOrderTax";
import { useLoadTaxInvoiceList } from "hooks/useOrders";
import { useAppSelector } from "store";
import { TaxAddressData } from "services/interface";
import MobileManage from "components/MobileManage";
import ContactNote from "./ContactNote";

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 },
  },
};

interface UpdateAgentProps {
  id: string;
  section?: string;
  tabsKey?: string;
}

const UpdateAgent: FC<UpdateAgentProps> = ({ id, section, tabsKey }) => {
  const factoryId = useAppSelector(
    (state) => state.auth.profile?.user?.role?.ref_id,
  );
  const navigate = useNavigate();
  const [dataAddress, setDataAddress] = useState<AddressData[]>([]);
  const [dataAddressTax, setDataAddressTax] = useState<TaxAddressData[]>([]);
  const [requestTax, setRequestTax] = useState<boolean>(false);
  const [addressIdSelected, setAddressIdSelected] = useState<string>("");
  const [secondaryMobile, setSecondaryMobile] = useState<string[]>([]);
  const { contact } = useAppSelector((state) => state.auth);

  const {
    data: agentData,
    isLoading,
    refetch: refetchAgent,
    isError,
  } = useGetAgentById(id);
  const {
    data: dataTax,
    isPending: taxPending,
    refetch: refetchTax,
  } = useLoadTaxInvoiceList({
    factory_id: factoryId ?? "",
    customer_id: id ?? "",
  });
  const [form] = Form.useForm();
  const { data: masterProviceData, isLoading: proviceLoading } =
    useGetMasterProvince();
  const { mutate: updateAgent } = useUpdateAgent();

  const handleRemoveItem = (target: string) => {
    const updatedDataAddress = dataAddress.filter(
      (item, index) => item.address_id !== target,
    );
    setDataAddress(updatedDataAddress);
  };

  useEffect(() => {
    if (id && dataTax?.data) {
      const newTaxAddresses: TaxAddressData[] = dataTax.data.map(
        (tax: TaxAddressData) => {
          return {
            id: tax.id,
            name: tax.name,
            address_text: tax.address_text,
            telephone: tax.telephone,
            fax: tax.fax,
            email: tax.email,
            tax_no: tax.tax_no,
            factory_id: factoryId ?? "",
            ref_id: id ?? "",
            ref_type: "agent",
            is_active: tax.is_active,
          };
        },
      );

      setDataAddressTax(newTaxAddresses);

      const activeAddress = newTaxAddresses.find((tax) => tax.is_active);
      if (activeAddress) {
        setAddressIdSelected(activeAddress.id);
      } else {
        setAddressIdSelected("");
      }
    }
  }, [dataTax]);

  useEffect(() => {
    if (agentData) {
      form.setFieldsValue({
        firstname: agentData?.data?.first_name || "",
        lastname: agentData?.data?.last_name || "",
        telephone: agentData?.data?.telephone_number || "",
        email: agentData?.data?.email || "",
        datasource: agentData?.data?.datasource || "",
        facebook: agentData?.data?.facebook || "",
        alias: agentData?.data?.alias || "",
      });
      // dataAddress
      const newAddresses: AddressData[] = agentData?.data?.address_ship?.map(
        (address) => {
          return {
            address_id: address.address_id,
            transport_type: address.transport_type,
            line_1: address.address_line_1,
            line_2: address.address_line_2,
            location_type: address.location_type,
            address_text: address.address_text,
            province_id: address.province_id,
            province_name: address.province_name,
            district_id: address.district_id,
            district_name: address.district_name,
            sub_district_id: address.sub_district_id,
            sub_district_name: address.sub_district_name,
            post_code: address.zip_code,
            address_type: address.address_type,
            address_name: address.address_name,
            address_telephone: address.address_telephone,
            address_remark: address.address_remark,
            is_default: address?.is_default,
            parking_id: address?.parking_id,
          };
        },
      );

      setDataAddress(newAddresses);

      setSecondaryMobile(agentData?.data?.contact_mobile ?? []);
    }
  }, [agentData]);

  const onFinish = (values: any) => {
    let addressData: AddressData | null = null;

    if (values.province_id) {
      addressData = {
        address_id: agentData?.data?.address_contact[0]?.address_id,
        line_1: values.address_text,
        province_id: values.province_id,
        district_id: values.district_id,
        sub_district_id: values.sub_district_id,
        post_code: values.zip_code,
        address_type: "contact",
        transport_type: values.transport,
      };
    }

    const dataRaw: any = {
      id: agentData?.data?.id,
      first_name: values.firstname,
      last_name: values.lastname,
      mobile: values.telephone,
      email: values.email,
      address: addressData ? addressData : null,
      datasource: values.datasource,
      facebook: values.facebook,
      ship_address: dataAddress,
      alias: values.alias,
      contact_mobile: secondaryMobile,
    };

    updateAgent(dataRaw, {
      onSuccess: (res) => {
        if (res.status === "success") {
          message.success("แก้ไขตัวแทนสำเร็จ");
        } else {
          message.error(`แก้ไขตัวแทนไม่สำเร็จ: ${res.error_message}`);
        }
      },
      onError: (error) => {
        message.error("แก้ไขตัวแทนไม่สำเร็จ");
      },
    });
    console.log(dataRaw);
  };

  const items = [
    {
      label: `ข้อมูลทั่วไป`,
      key: "0",
      children: (
        <>
          <MainCreateAgent
            form={form}
            masterProviceData={masterProviceData}
            dataAddress={agentData?.data?.address_contact[0]}
          />
          <Divider />
          <ContactNote refId={id} factoryId={factoryId ?? ""} />
          <Divider />

          <MobileManage
            primaryMobile={agentData?.data?.telephone_number ?? ""}
            secondaryMobile={secondaryMobile}
            setSecondaryMobile={setSecondaryMobile}
          />

          
          <>

          {contact?.code === "lunganek" ? (
            <>
            <Divider />
            <Form>
              <MainCreateOrderTax
                refetchData={refetchTax}
                dataAddressTax={dataAddressTax}
                setDataAddressTax={setDataAddressTax}
                refId={agentData?.data?.id ?? ""}
                refType={agentData?.data?.ref_type ?? ""}
                requestTax={requestTax}
                setRequestTax={setRequestTax}
                addressIdSelected={addressIdSelected}
                setAddressIdSelected={setAddressIdSelected}
              />
            </Form>
            </>
            ) : (
              <></>
            )}
            {contact?.code === "lunganek" ? (
              <>
                <Divider />
                <MainCreateBankingAgent agentId={agentData?.data?.id ?? ""} />
              </>
            ) : (
              <></>
            )}

            <Divider />
            <MainCreateAgentAddress
              id={id}
              form={form}
              dataAddress={dataAddress}
              handleRemoveItem={handleRemoveItem}
              setDataAddress={setDataAddress}
              masterProviceData={masterProviceData}
              searchOption={false}
              canSort={false}
              refetchData={refetchAgent}
              personAddress={agentData?.data?.address_contact[0]}
              addTemporary={false}
            />
          </>

          {contact?.code === "lunganek" ? (
            <FooterCreateAgent loading={proviceLoading} />
          ) : (
            <></>
          )}
        </>
      ),
    },
    {
      label: `รายการคำสั่งซื้อ`,
      key: "1",
      children: (
        <>
          <MainCreateAgentOrder
            id={id}
            dataAddress={dataAddress}
            canUpdate={false}
            canSort={true}
            tabsKey={tabsKey}
            refType={"agent"}
            userData={agentData}
          />
        </>
      ),
    },
    {
      label: `รายการส่งพัสดุ`,
      key: "2",
      children: (
        <AgentParcelTab
          agent_id={agentData?.data?.id ?? ""}
        />
      ),
    },
  ];
  return (
    <Form form={form} {...formItemLayout} onFinish={onFinish}>
      <Row className="rowgap-vbox" gutter={[24, 0]}>
        <Col span={24}>
          <Card bordered={false} className="criclebox h-full tabs-mobile">
            <HeaderTabs>
              <IconWrapper
                onClick={() => {
                  navigate("/manage-agent", { state: { fromDetail: true } });
                }}
              >
                <SpanIcon>{goBack}</SpanIcon>
              </IconWrapper>
              <Typography.Title level={5}>
                {contact?.code === "lunganek" ? "ข้อมูลตัวแทน" : "ข้อมูลลูกค้า"}
              </Typography.Title>
              <RightIconWrapper></RightIconWrapper>
            </HeaderTabs>
            <WrapperAll>
              <Tabs
                defaultActiveKey={section ? "1" : "0"}
                style={{ height: "100%" }}
                items={items}
                key="key"
              />
            </WrapperAll>
          </Card>
        </Col>
      </Row>
    </Form>
  );
};

export default UpdateAgent;

const Divider = styled.div`
  border-bottom: 0.5px solid #e8e8e8;
  margin: 10px 0px 10px 0px;
`;

const HeaderTabs = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  flex-direction: row;
  border-bottom: 1px solid #efefef;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 30px;
  border-radius: 6px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: #efefef;
  }
`;

const SpanIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const RightIconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const WrapperAll = styled.div`
  display: block;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`;
